import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const roquefortLesPinsEn: ChauffeurPage = {
  slug: "roquefort-les-pins",
  title: "VTC & private chauffeur in Roquefort-les-Pins",
  subtitle:
    "Private chauffeur in Roquefort-les-Pins: a green residential town between Nice, Grasse and Valbonne, with Nice airport transfers.",
  keywords: [
    "vtc roquefort-les-pins",
    "private chauffeur roquefort-les-pins",
    "roquefort les pins airport transfer",
    "sophia antipolis chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Roquefort-les-Pins",
    subtitle:
      "The green residential town between Nice, Grasse and Valbonne: Nice airport transfers, hourly hire and everyday travel.",
    keywords: [
      "VTC Roquefort-les-Pins",
      "residential chauffeur",
      "Nice airport transfer",
      "chauffeur between Nice and Valbonne",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Roquefort-les-Pins",
    badges: [
      "Green residential",
      "Between Nice & Valbonne",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Roquefort-les-Pins",
    items: [
      {
        title: "A green residential town",
        description:
          "Villas and estates scattered among the pines: drop-off at the gate and a dedicated chauffeur for families settled in Roquefort-les-Pins.",
      },
      {
        title: "Nice–Grasse–Valbonne crossroads",
        description:
          "A central position between the coast, the perfume capital and the technology park: the chauffeur links these hubs with no route worry.",
      },
      {
        title: "Nice airport ~35 min",
        description:
          "Reach Nice Côte d’Azur airport in about 35 minutes via Villeneuve-Loubet and the A8, with flight tracking.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "FR/EN, based on the French Riviera, punctual and discreet — 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Roquefort-les-Pins",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up in Roquefort-les-Pins with flight tracking, to and from Nice Côte d’Azur airport, in about 35 minutes.",
      },
      {
        title: "Residential hourly hire",
        text: "A dedicated chauffeur for owners and families: appointments, schools, errands and outings, with drop-off at the gate.",
      },
      {
        title: "Sophia Antipolis & Valbonne link",
        text: "Journeys to the technology park and neighbouring companies, for business meetings and professional travel.",
      },
      {
        title: "Nice, Cannes & Grasse shuttles",
        text: "Links to the coast and the back country, with a chauffeur who anticipates traffic at peak hours.",
      },
      {
        title: "Long distance",
        text: "Journeys of any distance from Roquefort-les-Pins: Monaco, Saint-Tropez, Italy and ski resorts, in a comfortable vehicle.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Roquefort-les-Pins village",
        description: "Heart of the town, shops and squares.",
      },
      {
        name: "Estates & villas among the pines",
        description: "Scattered residential quarters, drop-off at the gate.",
      },
      {
        name: "Le Rouret",
        description: "Neighbouring village, towards the Grasse back country.",
      },
      {
        name: "Villeneuve-Loubet",
        description: "Towards the sea and the A8 motorway axis.",
      },
      {
        name: "Sophia Antipolis",
        description: "The technology park nearby, companies and laboratories.",
      },
      {
        name: "Nice airport",
        description: "About 35 minutes via Villeneuve-Loubet and the A8.",
      },
    ],
  },
  generic: {
    heading: "Roquefort-les-Pins, green residential at the western crossroads",
    subheading:
      "A town of villas among the pines, ideally placed between Nice, Grasse and Valbonne.",
    paragraphs: [
      "Roquefort-les-Pins spreads its estates and villas among the pines, in a green residential setting favoured by families. For those who live here, a dedicated chauffeur simplifies daily life: appointments, schools, errands and outings, with drop-off at the gate and no parking worry.",
      "The town sits at an ideal crossroads between the Nice coast, the perfume capital and the technology park: Nice, Cannes, Grasse, Valbonne and Sophia Antipolis are within reach by road. Our chauffeurs anticipate traffic at peak hours to keep to your schedule.",
      "For Nice Côte d’Azur airport, allow around 35 minutes via Villeneuve-Loubet and the A8 depending on traffic; our bilingual chauffeurs track your flight and adjust the departure.",
    ],
    bullets: [
      "Drop-off at the gate",
      "Nice–Grasse–Valbonne crossroads",
      "Flight tracking & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Roquefort-les-Pins",
    steps: [
      "Share your route (home, Sophia Antipolis, Nice airport) and your times.",
      "We confirm the right vehicle and a fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you at the gate or the agreed address.",
      "Live adjustments to suit traffic and your programme.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Roquefort-les-Pins"),
    {
      q: "How long is it to Nice airport?",
      a: "Allow about 35 minutes via Villeneuve-Loubet and the A8 depending on traffic, with flight tracking and a personal welcome.",
    },
    {
      q: "Do you offer a dedicated chauffeur for resident families?",
      a: "Yes, for owners and families settled in Roquefort-les-Pins, we offer hourly hire with a dedicated chauffeur: appointments, schools, errands and outings, with drop-off at the gate.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the area’s events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class sedans, 7-seater van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Do you serve Sophia Antipolis and Valbonne from Roquefort-les-Pins?",
      a: "Yes, the technology park and Valbonne are a few minutes away; we provide transfers and hourly hire for your business meetings across the whole area.",
    },
  ],
  relatedLinks: [
    {
      label: "Car service on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Nice Airport shuttle",
      href: "/en/chauffeur/airport",
    },
    { label: "Chauffeur service Nice", href: "/en/chauffeur/nice" },
    { label: "Private car service in Cannes", href: "/en/chauffeur/cannes" },
  ],
  cta: {
    title: "Your private chauffeur in Roquefort-les-Pins",
    text: "Nice airport transfer, hourly hire or Sophia Antipolis link — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Roquefort-les-Pins — private chauffeur",
    description:
      "Private car service in Roquefort-les-Pins: Nice airport transfer in ~35 min, residential hourly hire, Sophia Antipolis link. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/roquefort-les-pins",
  },
};

export default roquefortLesPinsEn;
